import type { Product, ProductVendor } from "@models/product";
import { vendorRowsForSku } from "@utils/vendorRows";

// A row the admin sees: an active one. `vendors[]` is not filtered
// server-side, so a soft-deleted row (isActive false) is hidden, and so is a
// row that omits the flag, as in the products list's Vendor column.
const activeRows = (rows: ProductVendor[]) =>
    rows.filter((vendor) => vendor.isActive);

/**
 * The product's own vendor offerings (ruling I2): the active rows supplying
 * the BASE sku. A product made of variants has no base sku, so this is empty
 * for one; its offerings are per variant.
 */
export const activeBaseOfferings = (product: Product): ProductVendor[] =>
    activeRows(vendorRowsForSku(product.vendors, null));

/**
 * The product's Stock (DecisionLog §1.3, ruling I2): what its active vendor
 * rows can supply, summed over the base sku and every ACTIVE variant. An
 * inactive variant is not for sale, so its vendors' stock is not the
 * product's. Admins never type stock; it comes from the vendors.
 *
 * Null (shown as an em-dash) when there is no active row, or when a row does
 * not say how many it can supply: a total missing a row would read as a real
 * count.
 */
export const productStockTotal = (product: Product): number | null => {
    const rows = [
        ...activeBaseOfferings(product),
        ...product.variants
            .filter((variant) => variant.isActive)
            .flatMap((variant) =>
                activeRows(vendorRowsForSku(variant.vendors, variant.id)),
            ),
    ];
    const quantities = rows
        .map((row) => row.availableQuantity)
        .filter((quantity): quantity is number => quantity !== undefined);

    if (rows.length === 0 || quantities.length !== rows.length) {
        return null;
    }

    return quantities.reduce((total, quantity) => total + quantity, 0);
};
